import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";
import CryptoJS from "crypto-js";
import { API_ENDPOINTS, getFullUrl } from "config/api";

// MUI Components
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  Badge,
  Tooltip,
  Card,
  CardContent,
  Grid,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import SendIcon from "@mui/icons-material/Send";
import PeopleIcon from "@mui/icons-material/People";
import ChatIcon from "@mui/icons-material/Chat";
import RefreshIcon from "@mui/icons-material/Refresh";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CloseIcon from "@mui/icons-material/Close";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";

// Custom Components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Services & Hooks
import chatService from "API/chatService";
import { useSignalR } from "hooks/useSignalR";
import signalRManager from "services/signalRManager";

// AES Constants
const AES_KEY = "7D3B8E69F1A84B7EA8A3F6792B44C926";
const AES_IV = "A1B2C3D4E5F60718";

const decryptText = (encryptedText) => {
  const key = CryptoJS.enc.Utf8.parse(AES_KEY);
  const iv = CryptoJS.enc.Utf8.parse(AES_IV);
  const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  return decrypted.toString(CryptoJS.enc.Utf8);
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
};

const formatTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Format date header (Today, Yesterday, or date)
const formatDateHeader = (dateString) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const messageDate = new Date(date);
  messageDate.setHours(0, 0, 0, 0);
  
  if (messageDate.getTime() === today.getTime()) {
    return "Today";
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return "Yesterday";
  } else {
    // Format as "Month Day, Year" or "Month Day" if same year
    return messageDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: messageDate.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
    });
  }
};

// Check if two dates are on the same day
const isSameDay = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  d1.setHours(0, 0, 0, 0);
  d2.setHours(0, 0, 0, 0);
  return d1.getTime() === d2.getTime();
};

// Typing animation keyframes
const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
`;

const GlobalChat = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [message, setMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalMessages, setTotalMessages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chatStats, setChatStats] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editMessageText, setEditMessageText] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment"); // "user" for front, "environment" for back
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioRefs = useRef({});

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const {
    isConnected,
    messages,
    setMessages,
    onlineUsers,
    typingUsers,
    sendMessage,
    startTyping,
    stopTyping,
    connection,
  } = useSignalR();

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load chat history
  const loadChatHistory = useCallback(async (page = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const response = await chatService.getChatHistory(page, 50);
      
      if (append) {
        setChatHistory((prev) => [...(response.messages || []), ...prev]);
      } else {
        setChatHistory(response.messages || []);
      }
      
      setTotalMessages(response.totalCount || 0);
      setPageNumber(page);

      if (!append) {
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      safeToast.error("Failed to load chat history");
      console.error("Error loading chat history:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // Load more messages (pagination)
  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || loading) return;
    const nextPage = pageNumber + 1;
    const totalPages = Math.ceil(totalMessages / 50);
    if (nextPage > totalPages) return;

    await loadChatHistory(nextPage, true);
  }, [pageNumber, totalMessages, loadingMore, loading, loadChatHistory]);

  // Load chat stats
  const loadChatStats = useCallback(async () => {
    try {
      const stats = await chatService.getChatStats();
      setChatStats(stats);
    } catch (error) {
      console.error("Error loading chat stats:", error);
    }
  }, []);

  // Load current user info
  const loadCurrentUser = useCallback(async () => {
    try {
      const userInfo = await chatService.getUserInfo();
      setCurrentUser(userInfo);
    } catch (error) {
      console.error("Error loading user info:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const userDetails = localStorage.getItem("UserLogDetails");
    if (!userDetails) {
      safeToast.warn("Please login to access chat");
      navigate("/BasicAuth");
      return;
    }

    loadChatHistory();
    loadChatStats();
    loadCurrentUser();
  }, [navigate, loadChatHistory, loadChatStats, loadCurrentUser]);

  // Update chat history when new messages arrive via SignalR
  useEffect(() => {
    if (messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      setChatHistory((prev) => {
        // Check if message already exists
        if (prev.some((m) => m.messageId === latestMessage.messageId)) {
          return prev;
        }
        return [...prev, latestMessage];
      });
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages]);

  // Handle SignalR message deleted event
  useEffect(() => {
    if (!connection) return;

    const handleMessageDeleted = (messageId) => {
      setChatHistory((prev) => prev.filter((m) => m.messageId !== messageId));
      // No notification - silently update UI when other users delete messages
    };

    const handleMessageUpdated = (updatedMessage) => {
      setChatHistory((prev) =>
        prev.map((m) =>
          m.messageId === updatedMessage.messageId ? updatedMessage : m
        )
      );
      // No notification - silently update UI when other users edit messages
    };

    connection.on("MessageDeleted", handleMessageDeleted);
    connection.on("MessageUpdated", handleMessageUpdated);

    return () => {
      connection.off("MessageDeleted", handleMessageDeleted);
      connection.off("MessageUpdated", handleMessageUpdated);
    };
  }, [connection]);

  // Auto-scroll when new messages arrive or typing indicator appears
  useEffect(() => {
    scrollToBottom();
  }, [chatHistory, typingUsers]);

  // Handle send message
  const handleSendMessage = async () => {
    const isAudio = selectedImage?.type?.startsWith("audio/");
    if ((!message.trim() && !selectedImage) || sending || !isConnected) return;

    // Clear typing timeout when sending
    if (typingTimeout) {
      clearTimeout(typingTimeout);
      setTypingTimeout(null);
    }
    stopTyping();

    setSending(true);
    let imageUrl = null;
    let audioUrl = null;

    try {
      // Upload image or audio if selected
      if (selectedImage) {
        setUploadingImage(true);
        // Check if it's an audio file
        const isAudio = selectedImage.type?.startsWith("audio/");
        if (isAudio) {
          // Upload audio file to voice endpoint
          const uploadResult = await chatService.uploadVoice(selectedImage);
          audioUrl = uploadResult.audioUrl;
        } else {
          // Upload image file to image endpoint
          const uploadResult = await chatService.uploadImage(selectedImage);
          imageUrl = uploadResult.imageUrl;
        }
        setUploadingImage(false);
      }

      // Send message with or without image/audio
      if (isConnected && connection) {
        await signalRManager.sendMessage(message.trim() || "", imageUrl, audioUrl);
      } else {
        // Fallback to API if SignalR not connected
        const response = await fetch(API_ENDPOINTS.CHAT.SEND_MESSAGE, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${chatService.getToken()}`,
          },
          body: JSON.stringify({
            message: message.trim() || "",
            imageUrl: imageUrl,
            audioUrl: audioUrl,
          }),
        });
        if (!response.ok) throw new Error("Failed to send message");
      }

      // Clear inputs
      setMessage("");
      handleRemoveImage();
      inputRef.current?.focus();
    } catch (error) {
      safeToast.error("Failed to send message");
      console.error("Error sending message:", error);
      setUploadingImage(false);
    } finally {
      setSending(false);
    }
  };

  // Handle menu open
  const handleMenuOpen = (event, message) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedMessage(message);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    // Don't clear selectedMessage here - it might be needed for edit/delete dialogs
  };

  // Handle edit message
  const handleEditClick = () => {
    if (selectedMessage) {
      setEditMessageText(selectedMessage.message);
      setEditDialogOpen(true);
      setMenuAnchor(null); // Close menu but keep selectedMessage
    }
  };

  // Handle delete message
  const handleDeleteClick = () => {
    console.log("Delete menu item clicked, selectedMessage:", selectedMessage);
    setMenuAnchor(null); // Close menu but keep selectedMessage
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    console.log("handleConfirmDelete called", { selectedMessage, deleting });
    
    if (!selectedMessage) {
      console.error("No selectedMessage found!");
      safeToast.error("No message selected for deletion");
      setDeleteDialogOpen(false);
      return;
    }
    
    if (deleting) {
      console.log("Already deleting, skipping");
      return;
    }

    console.log("Delete confirmed for message:", selectedMessage);
    console.log("Message ID:", selectedMessage.messageId);
    setDeleting(true);
    
    try {
      // Always call API endpoint first to ensure database is updated
      console.log("Calling delete API for messageId:", selectedMessage.messageId);
      const result = await chatService.deleteMessage(selectedMessage.messageId);
      console.log("Delete API response:", result);
      
      // Update local state immediately
      setChatHistory((prev) => {
        const filtered = prev.filter((m) => m.messageId !== selectedMessage.messageId);
        console.log("Updated chat history, removed message:", selectedMessage.messageId);
        return filtered;
      });
      
      // Also broadcast via SignalR for real-time updates (if connected)
      if (isConnected && connection) {
        try {
          await signalRManager.deleteMessage(selectedMessage.messageId);
          console.log("SignalR delete broadcast sent");
        } catch (signalRError) {
          console.warn("SignalR broadcast failed, but API call succeeded:", signalRError);
        }
      }
      
      // Message deleted successfully - no notification needed
      const messageIdToClear = selectedMessage.messageId;
      setSelectedMessage(null);
      setDeleteDialogOpen(false);
      console.log("Delete completed successfully for messageId:", messageIdToClear);
    } catch (error) {
      console.error("Full delete error:", error);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      safeToast.error(error.response?.data?.error || error.message || "Failed to delete message");
    } finally {
      setDeleting(false);
    }
  };

  // Confirm edit
  const handleConfirmEdit = async () => {
    if (!selectedMessage || !editMessageText.trim() || editing) return;

    if (editMessageText.trim() === selectedMessage.message) {
      setEditDialogOpen(false);
      setEditMessageText("");
      setSelectedMessage(null);
      return;
    }

    console.log("Edit clicked for message:", selectedMessage);
    console.log("New message text:", editMessageText.trim());
    setEditing(true);
    try {
      // Always call API endpoint first to ensure database is updated
      console.log("Calling update API for messageId:", selectedMessage.messageId);
      const updated = await chatService.updateMessage(
        selectedMessage.messageId,
        editMessageText.trim()
      );
      console.log("Update API response:", updated);
      
      // Update local state immediately
      setChatHistory((prev) =>
        prev.map((m) => (m.messageId === updated.messageId ? updated : m))
      );
      
      // Also broadcast via SignalR for real-time updates (if connected)
      if (isConnected && connection) {
        try {
          await signalRManager.updateMessage(selectedMessage.messageId, editMessageText.trim());
        } catch (signalRError) {
          console.warn("SignalR broadcast failed, but API call succeeded:", signalRError);
        }
      }
      
      // Message updated successfully - no notification needed
      setEditMessageText("");
      setSelectedMessage(null);
      setEditDialogOpen(false);
    } catch (error) {
      console.error("Full update error:", error);
      console.error("Error response:", error.response);
      safeToast.error(error.response?.data?.error || "Failed to update message");
    } finally {
      setEditing(false);
    }
  };

  // Handle image selection (from gallery)
  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      safeToast.error("Invalid file type. Please select an image (JPG, PNG, GIF, WEBP)");
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      safeToast.error("Image size exceeds 5MB limit");
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setSelectedImage(file);
    };
    reader.readAsDataURL(file);
    
    // Reset input so same file can be selected again
    event.target.value = "";
  };

  // Open camera
  const handleOpenCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        safeToast.error("Camera access is not supported in your browser");
        return;
      }

      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setCameraOpen(true);

      // Wait for video element to be available
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error("Error accessing camera:", error);
      safeToast.error("Failed to access camera. Please check permissions.");
    }
  };

  // Close camera and stop stream
  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  // Switch camera (front/back)
  const handleSwitchCamera = async () => {
    const newFacingMode = facingMode === "environment" ? "user" : "environment";
    setFacingMode(newFacingMode);

    // Stop current stream
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    try {
      const constraints = {
        video: {
          facingMode: newFacingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (error) {
      console.error("Error switching camera:", error);
      safeToast.error("Failed to switch camera");
    }
  };

  // Capture photo from video stream
  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          safeToast.error("Failed to capture photo");
          return;
        }

        // Validate file size (5MB)
        if (blob.size > 5 * 1024 * 1024) {
          safeToast.error("Image size exceeds 5MB limit");
          return;
        }

        // Create file object from blob
        const file = new File([blob], "camera-photo.jpg", { type: "image/jpeg" });

        // Show preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(reader.result);
          setSelectedImage(file);
        };
        reader.readAsDataURL(file);

        // Close camera
        handleCloseCamera();
      },
      "image/jpeg",
      0.9
    );
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  // Start recording voice message
  const handleStartRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        safeToast.error("Microphone access is not supported in your browser");
        return;
      }

      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioRecorderRef.current = audioStream;

      const recorder = new MediaRecorder(audioStream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: "audio/webm" });
        
        // Create audio file
        const audioFile = new File([audioBlob], "voice-message.webm", { type: "audio/webm" });
        
        // Show preview and allow sending
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedImage(audioFile); // Reuse selectedImage state for audio
          setImagePreview(reader.result); // Reuse preview for audio URL
        };
        reader.readAsDataURL(audioBlob);

        // Stop all tracks
        audioStream.getTracks().forEach((track) => track.stop());
        setAudioChunks([]);
        setIsRecording(false);
        setRecordingTime(0);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setAudioChunks(chunks);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      safeToast.error("Failed to access microphone. Please check permissions.");
    }
  };

  // Stop recording voice message
  const handleStopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  // Cancel recording
  const handleCancelRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
    }
    if (audioRecorderRef.current) {
      audioRecorderRef.current.getTracks().forEach((track) => track.stop());
    }
    setAudioChunks([]);
    setIsRecording(false);
    setRecordingTime(0);
    setSelectedImage(null);
    setImagePreview(null);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  // Format recording time
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Play/pause audio
  const handleToggleAudio = (messageId, audioUrl) => {
    if (!audioRefs.current[messageId]) {
      audioRefs.current[messageId] = new Audio(audioUrl);
      audioRefs.current[messageId].onended = () => {
        setPlayingAudioId(null);
      };
    }

    const audio = audioRefs.current[messageId];

    if (playingAudioId === messageId) {
      audio.pause();
      setPlayingAudioId(null);
    } else {
      if (playingAudioId) {
        audioRefs.current[playingAudioId]?.pause();
      }
      audio.play();
      setPlayingAudioId(messageId);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.src = "";
        }
      });
    };
  }, []);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!isConnected) return;

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    startTyping();

    const timeout = setTimeout(() => {
      stopTyping();
      setTypingTimeout(null);
    }, 3000);

    setTypingTimeout(timeout);
  }, [isConnected, typingTimeout, startTyping, stopTyping]);

  // Cleanup typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeout) {
        clearTimeout(typingTimeout);
        stopTyping();
      }
    };
  }, [typingTimeout, stopTyping]);

  // Get current student ID (define early to avoid temporal dead zone)
  const currentStudentId = currentUser?.studentId || null;

  // Get typing users (excluding current user)
  const getTypingUsers = useCallback(() => {
    if (!typingUsers || typingUsers.length === 0) return [];
    if (!currentStudentId) return typingUsers; // If current user not loaded, show all
    return typingUsers.filter(user => {
      // Handle both number and string comparison
      const userId = typeof user.studentId === 'string' ? parseInt(user.studentId) : user.studentId;
      return userId !== currentStudentId;
    });
  }, [typingUsers, currentStudentId]);

  // Check if typing indicator should show
  const shouldShowTypingIndicator = useCallback(() => {
    const otherTypingUsers = getTypingUsers();
    return otherTypingUsers && otherTypingUsers.length > 0;
  }, [getTypingUsers]);

  // Get user image URL
  const getUserImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("http")) return imageUrl;
    if (imageUrl.startsWith("/uploads/")) {
      return getFullUrl(imageUrl);
    }
    return getFullUrl(`/uploads/${imageUrl}`);
  };

  // Debug typing users changes
  useEffect(() => {
    console.log("Typing users state:", typingUsers);
    console.log("Current student ID:", currentStudentId);
    if (typingUsers && typingUsers.length > 0) {
      const filtered = getTypingUsers();
      console.log("Filtered typing users:", filtered);
      console.log("Should show typing indicator:", filtered.length > 0);
    }
  }, [typingUsers, currentStudentId, getTypingUsers]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={2}>
          {/* Main Chat Area */}
          <Grid item xs={12} md={8}>
            <Card
              elevation={0}
              sx={{
                height: "calc(100vh - 200px)",
                display: "flex",
                flexDirection: "column",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Compact Header */}
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: isConnected ? "success.main" : "warning.main",
                      ml: 0.5,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Global Chat
                  </Typography>
                  <Chip
                    label={isConnected ? "Live" : "Connecting"}
                    size="small"
                    color={isConnected ? "success" : "warning"}
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {chatStats && (
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        mr: 1,
                      }}
                    >
                      {chatStats.totalMessages} messages
                    </Typography>
                  )}
                  <Tooltip title="Refresh">
                    <IconButton
                      size="small"
                      onClick={() => loadChatHistory(1, false)}
                      sx={{ p: 0.75 }}
                    >
                      <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Messages Container */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2,
                  bgcolor: alpha("#f5f5f5", 0.3),
                  "&::-webkit-scrollbar": {
                    width: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    bgcolor: "transparent",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: alpha("#000", 0.2),
                    borderRadius: "3px",
                    "&:hover": {
                      bgcolor: alpha("#000", 0.3),
                    },
                  },
                }}
              >
                {loading ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : chatHistory.length === 0 ? (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.875rem",
                      }}
                    >
                      No messages yet. Start the conversation!
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {totalMessages > chatHistory.length && (
                      <Box textAlign="center" mb={1.5}>
                        <MDButton
                          variant="text"
                          size="small"
                          onClick={loadMoreMessages}
                          disabled={loadingMore}
                          sx={{
                            fontSize: "0.75rem",
                            textTransform: "none",
                            minWidth: "auto",
                            px: 2,
                          }}
                        >
                          {loadingMore ? (
                            <CircularProgress size={16} />
                          ) : (
                            "Load older messages"
                          )}
                        </MDButton>
                      </Box>
                    )}
                    {chatHistory.map((msg, index) => {
                      // Compare student IDs - handle both number and string types
                      const msgStudentId = typeof msg.studentId === 'string' ? parseInt(msg.studentId) : msg.studentId;
                      const currentId = typeof currentStudentId === 'string' ? parseInt(currentStudentId) : currentStudentId;
                      const isCurrentUser = msgStudentId === currentId;
                      const imageUrl = getUserImageUrl(msg.profileImageUrl);
                      const prevMessage = chatHistory[index - 1];
                      const showAvatar =
                        !prevMessage ||
                        prevMessage.studentId !== msg.studentId ||
                        new Date(msg.sentDateTime).getTime() -
                          new Date(prevMessage.sentDateTime).getTime() >
                          300000; // 5 minutes
                      
                      // Check if we need to show a date separator
                      const showDateSeparator = !prevMessage || !isSameDay(msg.sentDateTime, prevMessage.sentDateTime);

                      return (
                        <React.Fragment key={msg.messageId || index}>
                          {/* Date Separator */}
                          {showDateSeparator && (
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              my={1.5}
                              sx={{ width: "100%" }}
                            >
                              <Chip
                                label={formatDateHeader(msg.sentDateTime)}
                                size="small"
                                sx={{
                                  fontSize: "0.6875rem",
                                  height: 24,
                                  bgcolor: "background.default",
                                  color: "text.secondary",
                                  fontWeight: 500,
                                  border: "none",
                                  "& .MuiChip-label": {
                                    px: 1.5,
                                  },
                                }}
                              />
                            </Box>
                          )}
                          <Box
                            display="flex"
                            gap={0.75}
                            mb={showAvatar ? 1 : 0.4}
                            sx={{
                              flexDirection: isCurrentUser ? "row-reverse" : "row",
                            }}
                          >
                          {/* Avatar - only show if needed */}
                          <Box
                            sx={{
                              width: showAvatar ? 28 : 0,
                              transition: "width 0.2s",
                            }}
                          >
                            {showAvatar && (
                              <Avatar
                                src={imageUrl}
                                sx={{
                                  width: 28,
                                  height: 28,
                                  fontSize: "0.7rem",
                                  border: "1.5px solid",
                                  borderColor: "background.paper",
                                }}
                              >
                                {(msg.studentName || msg.userName || "?")
                                  .charAt(0)
                                  .toUpperCase()}
                              </Avatar>
                            )}
                          </Box>

                          {/* Message Content */}
                          <Box
                            sx={{
                              flex: 1,
                              maxWidth: isCurrentUser ? "75%" : "85%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: isCurrentUser ? "flex-end" : "flex-start",
                            }}
                          >
                            {showAvatar && !isCurrentUser && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.65rem",
                                  fontWeight: 600,
                                  color: "text.primary",
                                  mb: 0.2,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.4,
                                }}
                              >
                                {msg.studentName || msg.userName || "Anonymous"}
                                <Typography
                                  component="span"
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.6rem",
                                    fontWeight: 400,
                                    color: "text.secondary",
                                  }}
                                >
                                  {formatTime(msg.sentDateTime)}
                                </Typography>
                              </Typography>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 0.5,
                                position: "relative",
                                width: "100%",
                                "&:hover .message-actions": {
                                  opacity: 1,
                                  visibility: "visible",
                                },
                              }}
                            >
                              <Paper
                                elevation={0}
                                sx={{
                                  p: 0.75,
                                  px: 1,
                                  bgcolor: isCurrentUser
                                    ? "success.main"
                                    : "background.paper",
                                  color: isCurrentUser
                                    ? "success.contrastText"
                                    : "text.primary",
                                  borderRadius: isCurrentUser
                                    ? "12px 12px 4px 12px"
                                    : "12px 12px 12px 4px",
                                  border: isCurrentUser
                                    ? "none"
                                    : "1px solid",
                                  borderColor: isCurrentUser
                                    ? "transparent"
                                    : "divider",
                                  maxWidth: "100%",
                                  wordBreak: "break-word",
                                  display: "inline-block",
                                  flex: 1,
                                }}
                              >
                                {/* Image */}
                                {msg.imageUrl && (
                                  <Box sx={{ mb: msg.message ? 0.75 : 0 }}>
                                    <img
                                      src={getFullUrl(msg.imageUrl)}
                                      alt="Shared image"
                                      style={{
                                        maxWidth: "100%",
                                        maxHeight: "300px",
                                        borderRadius: "8px",
                                        display: "block",
                                        cursor: "pointer",
                                      }}
                                      onClick={() => {
                                        window.open(
                                          getFullUrl(msg.imageUrl),
                                          "_blank"
                                        );
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                      }}
                                    />
                                  </Box>
                                )}
                                {/* Audio/Voice Message */}
                                {msg.audioUrl && (
                                  <Box
                                    sx={{
                                      mb: msg.message ? 0.75 : 0,
                                      minWidth: "200px",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    <audio
                                      ref={(el) => {
                                        if (el) audioRefs.current[msg.messageId] = el;
                                      }}
                                      src={getFullUrl(msg.audioUrl)}
                                      controls
                                      style={{
                                        width: "100%",
                                        height: "32px",
                                      }}
                                      onPlay={() => {
                                        // Stop other audio if playing
                                        Object.keys(audioRefs.current).forEach((id) => {
                                          if (id !== msg.messageId.toString() && audioRefs.current[id]) {
                                            audioRefs.current[id].pause();
                                          }
                                        });
                                        setPlayingAudioId(msg.messageId);
                                      }}
                                      onPause={() => {
                                        if (playingAudioId === msg.messageId) {
                                          setPlayingAudioId(null);
                                        }
                                      }}
                                      onEnded={() => {
                                        setPlayingAudioId(null);
                                      }}
                                    />
                                  </Box>
                                )}
                                {/* Message text */}
                                {msg.message && (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.8125rem",
                                      lineHeight: 1.4,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {msg.message}
                                  </Typography>
                                )}
                              </Paper>
                              {isCurrentUser && (
                                <IconButton
                                  size="small"
                                  className="message-actions"
                                  onClick={(e) => handleMenuOpen(e, msg)}
                                  sx={{
                                    opacity: 0.7,
                                    visibility: "visible",
                                    transition: "opacity 0.2s",
                                    p: 0.5,
                                    minWidth: "auto",
                                    width: 24,
                                    height: 24,
                                    color: isCurrentUser
                                      ? "rgba(0, 0, 0, 0.7)"
                                      : "text.secondary",
                                    "&:hover": {
                                      opacity: 1,
                                      bgcolor: isCurrentUser
                                        ? "rgba(0, 0, 0, 0.08)"
                                        : "rgba(0, 0, 0, 0.05)",
                                      color: isCurrentUser
                                        ? "rgba(0, 0, 0, 0.9)"
                                        : "text.primary",
                                    },
                                  }}
                                >
                                  <MoreVertIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              )}
                            </Box>

                            {showAvatar && isCurrentUser && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.6rem",
                                  color: "text.secondary",
                                  mt: 0.2,
                                  mr: 0.4,
                                }}
                              >
                                {formatDate(msg.sentDateTime)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </React.Fragment>
                    );
                    })}

                    {/* Typing Indicator - WhatsApp Style */}
                    {(() => {
                      const typingUsersList = getTypingUsers();
                      if (!typingUsersList || typingUsersList.length === 0) return null;
                      
                      return (
                        <Box
                          display="flex"
                          gap={1}
                          mb={1.5}
                          sx={{
                            flexDirection: "row",
                          }}
                        >
                          {/* Avatar */}
                          <Box
                            sx={{
                              width: 32,
                            }}
                          >
                            <Avatar
                              src={getUserImageUrl(typingUsersList[0].profileImageUrl)}
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: "0.75rem",
                                border: "2px solid",
                                borderColor: "background.paper",
                              }}
                            >
                              {(typingUsersList[0].studentName || typingUsersList[0].userName || "?")
                                .charAt(0)
                                .toUpperCase()}
                            </Avatar>
                          </Box>

                          {/* Typing Bubble */}
                          <Box
                            sx={{
                              flex: 1,
                              maxWidth: "85%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            {typingUsersList.length === 1 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  color: "text.primary",
                                  mb: 0.25,
                                }}
                              >
                                {typingUsersList[0].studentName || typingUsersList[0].userName || "Anonymous"}
                              </Typography>
                            )}
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                px: 2,
                                bgcolor: "background.paper",
                                borderRadius: "18px 18px 18px 4px",
                                border: "1px solid",
                                borderColor: "divider",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Box
                                display="flex"
                                gap={0.3}
                                alignItems="center"
                                sx={{
                                  "& span": {
                                    width: 6,
                                    height: 6,
                                    borderRadius: "50%",
                                    bgcolor: "text.secondary",
                                    display: "inline-block",
                                    animation: `${typingAnimation} 1.4s infinite ease-in-out`,
                                    "&:nth-of-type(1)": {
                                      animationDelay: "0s",
                                    },
                                    "&:nth-of-type(2)": {
                                      animationDelay: "0.2s",
                                    },
                                    "&:nth-of-type(3)": {
                                      animationDelay: "0.4s",
                                    },
                                  },
                                }}
                              >
                                <span />
                                <span />
                                <span />
                              </Box>
                            </Paper>
                          </Box>
                        </Box>
                      );
                    })()}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </Box>

              {/* Message Input */}
              <Box
                sx={{
                  p: 1.5,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                {/* Image Preview (only for images, not audio) */}
                {imagePreview && selectedImage && !selectedImage.type?.startsWith("audio/") && (
                  <Box
                    sx={{
                      mb: 1,
                      position: "relative",
                      display: "inline-block",
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "200px",
                        maxHeight: "200px",
                        display: "block",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "rgba(0, 0, 0, 0.5)",
                        color: "white",
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.7)",
                        },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {/* Recording Indicator */}
                {isRecording && (
                  <Box
                    sx={{
                      mb: 1,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      bgcolor: "error.light",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "error.main",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <FiberManualRecordIcon
                        sx={{
                          color: "error.main",
                          fontSize: 16,
                          animation: "pulse 1.5s infinite",
                          "@keyframes pulse": {
                            "0%, 100%": { opacity: 1 },
                            "50%": { opacity: 0.3 },
                          },
                        }}
                      />
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                        Recording: {formatRecordingTime(recordingTime)}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={handleStopRecording}
                        sx={{
                          bgcolor: "error.main",
                          color: "white",
                          "&:hover": { bgcolor: "error.dark" },
                        }}
                      >
                        <StopIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelRecording}
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                )}
                {/* Audio Preview */}
                {imagePreview && selectedImage?.type?.startsWith("audio/") && (
                  <Box
                    sx={{
                      mb: 1,
                      p: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      bgcolor: "background.default",
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (audioRefs.current["preview"]) {
                          audioRefs.current["preview"].pause();
                          audioRefs.current["preview"] = null;
                        }
                        handleRemoveImage();
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    <audio
                      ref={(el) => {
                        if (el) audioRefs.current["preview"] = el;
                      }}
                      src={imagePreview}
                      controls
                      style={{ flex: 1, height: 32 }}
                    />
                  </Box>
                )}
                <Box display="flex" gap={1} alignItems="flex-end">
                  {/* Hidden file input for gallery */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageSelect}
                    style={{ display: "none" }}
                  />
                  <IconButton
                    color="default"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={!isConnected || sending || uploadingImage || isRecording}
                    sx={{
                      color: "text.secondary",
                    }}
                    title="Choose from gallery"
                  >
                    <ImageIcon />
                  </IconButton>
                  <IconButton
                    color="default"
                    onClick={handleOpenCamera}
                    disabled={!isConnected || sending || uploadingImage || isRecording}
                    sx={{
                      color: "text.secondary",
                    }}
                    title="Take a photo"
                  >
                    <CameraAltIcon />
                  </IconButton>
                  <IconButton
                    color={isRecording ? "error" : "default"}
                    onClick={isRecording ? handleStopRecording : handleStartRecording}
                    disabled={!isConnected || sending || uploadingImage || isRecording}
                    sx={{
                      color: isRecording ? "error.main" : "text.secondary",
                    }}
                    title={isRecording ? "Stop recording" : "Record voice message"}
                  >
                    <MicIcon />
                  </IconButton>
                  <TextField
                    ref={inputRef}
                    fullWidth
                    placeholder={
                      isConnected
                        ? "Type a message..."
                        : "Connecting..."
                    }
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      handleTyping();
                    }}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    onKeyUp={(e) => {
                      // Stop typing when user clears the input
                      if (e.target.value.trim().length === 0 && typingTimeout) {
                        clearTimeout(typingTimeout);
                        stopTyping();
                        setTypingTimeout(null);
                      }
                    }}
                    disabled={!isConnected || sending || uploadingImage || isRecording}
                    size="small"
                    multiline
                    maxRows={4}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: "background.default",
                        fontSize: "0.875rem",
                        "& fieldset": {
                          borderColor: "divider",
                        },
                        "&:hover fieldset": {
                          borderColor: "success.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "success.main",
                        },
                      },
                    }}
                  />
                  <IconButton
                    color="success"
                    onClick={handleSendMessage}
                    disabled={
                      !isConnected ||
                      sending ||
                      uploadingImage ||
                      isRecording ||
                      (!message.trim() && !selectedImage)
                    }
                    sx={{
                      bgcolor: "success.main",
                      color: "white",
                      "&:hover": {
                        bgcolor: "success.dark",
                        color: "white",
                      },
                      "&:disabled": {
                        bgcolor: "action.disabledBackground",
                        color: "action.disabled",
                      },
                      p: 1.25,
                      "& svg": {
                        color: "white !important",
                      },
                    }}
                  >
                    {sending || uploadingImage ? (
                      <CircularProgress size={18} sx={{ color: "white !important" }} />
                    ) : (
                      <SendIcon sx={{ fontSize: 18, color: "white !important" }} />
                    )}
                  </IconButton>
                </Box>
              </Box>
            </Card>
          </Grid>

          {/* Sidebar - Online Users & Stats */}
          <Grid item xs={12} md={4}>
            {/* Online Users */}
            <Card
              elevation={0}
              sx={{
                mb: 2,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: "text.secondary",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 14 }} />
                  Online · {onlineUsers.length}
                </Typography>
              </Box>
              <Box
                sx={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  p: 1.5,
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: alpha("#000", 0.2),
                    borderRadius: "2px",
                  },
                }}
              >
                {onlineUsers.length === 0 ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    No users online
                  </Typography>
                ) : (
                  onlineUsers.map((user) => {
                    const imageUrl = getUserImageUrl(user.profileImageUrl);
                    return (
                      <Box
                        key={user.studentId}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mb={1}
                        sx={{
                          "&:last-child": { mb: 0 },
                        }}
                      >
                        <Badge
                          overlap="circular"
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                          }}
                          badgeContent={
                            <FiberManualRecordIcon
                              sx={{
                                fontSize: 10,
                                color: "success.main",
                              }}
                            />
                          }
                        >
                          <Avatar
                            src={imageUrl}
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: "0.75rem",
                              border: "2px solid",
                              borderColor: "background.paper",
                            }}
                          >
                            {(user.studentName || user.userName || "?")
                              .charAt(0)
                              .toUpperCase()}
                          </Avatar>
                        </Badge>
                        <Box flex={1} sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8125rem",
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {user.studentName || user.userName || "Anonymous"}
                          </Typography>
                          {user.userName && user.studentName && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.7rem",
                                color: "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                              }}
                            >
                              @{user.userName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Card>

            {/* Chat Stats */}
            {chatStats && (
              <Card
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Statistics
                  </Typography>
                </Box>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          display: "block",
                          mb: 0.25,
                        }}
                      >
                        Messages
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.9375rem",
                          fontWeight: 600,
                        }}
                      >
                        {chatStats.totalMessages || 0}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.7rem",
                          color: "text.secondary",
                          display: "block",
                          mb: 0.25,
                        }}
                      >
                        Users
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.9375rem",
                          fontWeight: 600,
                        }}
                      >
                        {chatStats.totalRegisteredUsers || 0}
                      </Typography>
                    </Box>
                    {chatStats.lastMessageDateTime && (
                      <>
                        <Divider sx={{ my: 0.5 }} />
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.7rem",
                              color: "text.secondary",
                              display: "block",
                              mb: 0.25,
                            }}
                          >
                            Last activity
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8125rem",
                            }}
                          >
                            {formatDate(chatStats.lastMessageDateTime)}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Message Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: selectedMessage?.studentId === currentStudentId ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: selectedMessage?.studentId === currentStudentId ? "right" : "left",
        }}
      >
        {/* Only show Edit option for text messages (no image or audio) */}
        {!selectedMessage?.imageUrl && !selectedMessage?.audioUrl && (
          <MenuItem onClick={handleEditClick}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              <Typography sx={{ fontSize: "0.8125rem" }}>Edit</Typography>
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>
            <Typography color="error" sx={{ fontSize: "0.8125rem" }}>Delete</Typography>
          </ListItemText>
        </MenuItem>
      </Menu>

      {/* Camera Dialog */}
      <Dialog
        open={cameraOpen}
        onClose={handleCloseCamera}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "black",
            borderRadius: 2,
          },
        }}
      >
        <DialogContent sx={{ p: 0, position: "relative", bgcolor: "black" }}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              paddingTop: "75%", // 4:3 aspect ratio
              bgcolor: "black",
            }}
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
            <canvas ref={canvasRef} style={{ display: "none" }} />
            
            {/* Camera Controls */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                p: 2,
                bgcolor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <IconButton
                onClick={handleCloseCamera}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
                }}
              >
                <CloseIcon />
              </IconButton>
              
              <IconButton
                onClick={handleCapturePhoto}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "white",
                  border: "4px solid",
                  borderColor: "grey.300",
                  "&:hover": { bgcolor: "grey.100" },
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    bgcolor: "white",
                    border: "2px solid",
                    borderColor: "grey.400",
                  }}
                />
              </IconButton>
              
              <IconButton
                onClick={handleSwitchCamera}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255, 255, 255, 0.2)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.3)" },
                }}
                title="Switch camera"
              >
                <CameraAltIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Message Dialog - Responsive */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)} 
        fullWidth 
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: isMobile ? 0 : 2,
            maxHeight: isMobile ? "100%" : "90vh",
            width: isMobile ? "100%" : "auto",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5, px: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" component="div" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Edit Message
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <TextField
            autoFocus={!isMobile}
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={isMobile ? 8 : 4}
            value={editMessageText}
            onChange={(e) => setEditMessageText(e.target.value)}
            disabled={editing}
            onKeyPress={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                handleConfirmEdit();
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: { xs: "0.9rem", sm: "1rem" },
              },
              "& .MuiInputLabel-root": {
                fontSize: { xs: "0.9rem", sm: "1rem" },
              },
            }}
          />
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            pb: { xs: 2, sm: 2 },
            pt: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 0 },
            "& > button": {
              width: { xs: "100%", sm: "auto" },
              m: { xs: 0, sm: "0 8px" },
              minHeight: { xs: "44px", sm: "auto" }, // Better touch target on mobile
            }
          }}
        >
          <MDButton 
            onClick={() => setEditDialogOpen(false)} 
            disabled={editing}
            variant="outlined"
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              order: { xs: 2, sm: 1 }
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmEdit}
            color="primary"
            variant="gradient"
            disabled={!editMessageText.trim() || editing}
            startIcon={editing ? <CircularProgress size={16} /> : null}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              order: { xs: 1, sm: 2 }
            }}
          >
            {editing ? "Updating..." : "Update"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog - Responsive */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: isMobile ? 0 : 2,
            maxHeight: isMobile ? "100%" : "auto",
            width: isMobile ? "100%" : "auto",
            maxWidth: { xs: "100%", sm: "400px" },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5, px: { xs: 2, sm: 3 } }}>
          <Typography variant="h6" component="div" sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
            Delete Message?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <DialogContentText 
            sx={{ 
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: { xs: 1.5, sm: 1.6 }
            }}
          >
            Are you sure you want to delete this message? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions 
          sx={{ 
            px: { xs: 2, sm: 3 }, 
            pb: { xs: 2, sm: 2 },
            pt: { xs: 1, sm: 1 },
            flexDirection: { xs: "column-reverse", sm: "row" },
            gap: { xs: 1, sm: 0 },
            "& > button": {
              width: { xs: "100%", sm: "auto" },
              m: { xs: 0, sm: "0 8px" },
              minHeight: { xs: "44px", sm: "auto" }, // Better touch target on mobile
            }
          }}
        >
          <MDButton 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={deleting}
            variant="outlined"
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              order: { xs: 2, sm: 1 }
            }}
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={handleConfirmDelete}
            color="error"
            variant="gradient"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            fullWidth={isMobile}
            sx={{ 
              minWidth: { xs: "100%", sm: "auto" },
              order: { xs: 1, sm: 2 }
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default GlobalChat;
